import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { Product, ProductGroup, ProductPredefinedAmount, roundToCents, Supplement } from '@plys/src/models/product';
import { DeliveryType, EnvConfig, ProductDisplayStep } from '@shared/definitions';
import { map, Observable } from 'rxjs';

import { SKIP_ERROR_INTERCEPTOR } from '../application/errors/error.interceptor';
import { CartItem, CartStatus, resolveRowDiscount } from '../models/cart-item';
import { ProductAvailability } from '../models/product';
import { cartRequiresAccount } from '../models/subscription';
import {
  CartDiscountCode,
  CartDto,
  CartSummaryLineDto,
  CartSummaryProductDto,
  ProductAvailabilityDto,
  ProductGroupDto,
  ProductReservationResponse,
  ProductResponse,
  SubscriptionCardAttachDto,
  SupplementResponse,
} from './dto/products.dto';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly baseUrl = `${this.config.api}/v1.0`;

  constructor(@Inject(ENV_CONFIG) private readonly config: EnvConfig, private readonly client: HttpClient) {}

  /**
   * Prefetched during HTML parse by the inline script in the three `index.*.html` files and claimed by
   * `BootstrapPrefetchInterceptor`, which matches this URL and its `pos` / `include_availability` params
   * literally. Change either and the head start is silently lost — the call just runs normally — so keep
   * the three index files and the interceptor in sync. See `application/bootstrap-prefetch.ts`.
   */
  getProductsByShow(showId: string, posId: string): Observable<Product[]> {
    return this.client
      .get<{ data: ProductResponse[] }>(`${this.baseUrl}/shows/${showId}/products`, {
        params: {
          pos: posId,
          include_availability: true,
        },
      })
      .pipe(
        map((response) => response.data),
        map((items) => items.map((item) => this.mapToProductModel(item))),
        map((items) => items.sort((a, b) => a.order - b.order))
      );
  }

  getSupplements(productIds: string[], displayStep: ProductDisplayStep, showId: string): Observable<Supplement[]> {
    return this.client
      .post<{ data: SupplementResponse[] }>(`${this.baseUrl}/products/supplements`, {
        product_ids: productIds,
        depth: 2,
        show_id: showId,
        step: displayStep,
      })
      .pipe(
        map((response) => response?.data || []),
        map((items) => items.map((item) => this.mapToSupplementModel(item)))
      );
  }

  /**
   * Prefetched during HTML parse by the inline script in the three `index.*.html` files and claimed
   * by `BootstrapPrefetchInterceptor`, which matches this URL literally. Change it and the head
   * start is silently lost — the call just runs normally — so keep the three index files and
   * the interceptor in sync. See `application/bootstrap-prefetch.ts`.
   */
  getAddonsByPosId(id: string): Observable<Product[]> {
    return this.client.get<{ data: ProductResponse[] }>(`${this.config.api}/v1.0/pos/${id}/add-ons`).pipe(
      map((response) => response.data),
      map((items) => items.map((item) => this.mapToProductModel(item)))
    );
  }

  /**
   * `subscription_card_code` is optional here and deliberately never sent: the webshop adds a
   * flagged ticket first and redeems the card afterwards through {@link setSubscriptionCard}, so
   * the buyer is not stopped at add-to-cart for a card they have not produced yet (PAUL-2844).
   */
  makeReservation(
    productId: string,
    cartId: string,
    posId: string,
    showId: string,
    parentCartLineId = null,
    seatId: string = null,
    sessionId: string = null
  ): Observable<CartStatus> {
    return this.client
      .post<ProductReservationResponse>(`${this.baseUrl}/reservations`, {
        product_id: productId,
        cart_id: cartId,
        show_id: showId,
        delivery_method: DeliveryType.Digital,
        pos_id: posId,
        parent_cart_line_id: parentCartLineId,
        seat_id: seatId,
        ...(sessionId ? { shop_session_id: sessionId } : {}),
      })
      .pipe(map((res) => this.mapToCartStatus(res.cart_line_id, res.cart_id, res.cart)));
  }

  /**
   * Reserves a product that belongs to no show — a gift card sold by the prepaid shop. The payload
   * deliberately matches the subscription reservation (no show_id, seat_id or parent line) rather
   * than sending nulls, so both showless flows hit the backend the same way.
   *
   * `customValue` is the amount a gift card sold by predefined amounts is bought at, and omitting it
   * for such a product is never "use the default price": on an empty cart the line takes the
   * catalogue price (`0.00`, a free gift card) and on a cart already holding the product the backend
   * copies that cart's first line amount onto the new one. It stays out of the payload only for a
   * product priced normally, where the price *is* the amount (PAUL-3071).
   */
  makeShowlessReservation(
    productId: string,
    posId: string,
    cartId: string | null,
    customValue: number | null = null
  ): Observable<CartStatus> {
    return this.client
      .post<ProductReservationResponse>(`${this.baseUrl}/reservations`, {
        product_id: productId,
        pos_id: posId,
        cart_id: cartId,
        delivery_method: DeliveryType.Digital,
        ...(customValue === null ? {} : { custom_value: customValue }),
      })
      .pipe(map((res) => this.mapToCartStatus(res.cart_line_id, res.cart_id, res.cart)));
  }

  private mapToCartStatus(cart_line_id: string, cart_id: string, cart: CartDto): CartStatus {
    const discounts = cart.summary.discounts ?? [];
    const rowProductIds = new Set(cart.summary.products.map((product) => product.product_id));
    return {
      id: cart_id,
      reservationId: cart_line_id,
      total: cart.summary.total_amount,
      pending_amount: cart.summary.total_amount,
      total_recurring_monthly: cart.summary.total_recurring_monthly ?? 0,
      lines: cart.summary.lines,
      reserved_until: cart.reserved_until,
      server_time: cart.server_time,
      status: cart.status,
      discounts,
      items: cart.summary.products.map((product) =>
        this.mapToCartItem(product, cart.summary.lines, discounts, cart_line_id, rowProductIds)
      ),
      requiresAccount: cartRequiresAccount(cart.summary.products),
      integration_reservations: cart.summary.integration_reservations,
    };
  }

  private mapToCartItem(
    product: CartSummaryProductDto,
    lines: CartSummaryLineDto[],
    discounts: CartDiscountCode[],
    parentCartLineId: string,
    rowProductIds: ReadonlySet<string>
  ): CartItem {
    const productLines = lines.filter((line) => line.product_id === product.product_id);
    const { discountName, discountedAmount } = resolveRowDiscount(
      productLines.map((line) => line.id),
      lines,
      discounts,
      rowProductIds
    );

    return {
      productId: product.product_id,
      lines: productLines,
      parentCartLineId: parentCartLineId,
      title: product.title,
      amount: product?.amount,
      quantity: product?.quantity,
      total: product?.total,
      type: product?.type,
      discountName,
      discountedAmount,
    };
  }

  removeReservation(reservationId: string, posId: string, showId: string): Observable<CartStatus> {
    return this.client
      .delete<{ cart_line_id: string; cart: CartDto }>(`${this.baseUrl}/reservations/${reservationId}`, {
        body: {
          pos_id: posId,
          show_id: showId,
        },
      })
      .pipe(map((res) => this.mapToCartStatus(res.cart_line_id, res.cart.id, res.cart)));
  }

  addCvCart(cartLineId: string, params: { cineville_barcode: string; shop_id: string }) {
    return this.client.put<{ cineville_barcode: string; customer_id: string }>(
      `${this.baseUrl}/reservations/${cartLineId}`,
      params,
      {
        context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
      }
    );
  }

  /**
   * Attaches a subscription card to a line already in the cart, or detaches it when the code is
   * omitted (PAUL-2844). The card is optional on `POST /reservations`, so the line is added first
   * and redeemed here — the Cineville shape, and the analogue of {@link addCvCart}. `shop_id` scopes
   * an anonymous caller to the line's own tenant. Errors are shown inline on the information page
   * row, hence the skipped interceptor.
   */
  setSubscriptionCard(
    cartLineId: string,
    params: { shop_id: string; subscription_card_code?: string }
  ): Observable<SubscriptionCardAttachDto> {
    return this.client.put<SubscriptionCardAttachDto>(
      `${this.baseUrl}/reservations/${cartLineId}/subscription-card`,
      params,
      {
        context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
      }
    );
  }

  private mapToSupplementModel(dto: SupplementResponse): Supplement {
    return {
      id: dto.id,
      companyId: dto.company_id,
      name: dto.public_name,
      price: dto.current_price.total_amount,
      priceBefore: dto.current_price.regular_amount,
      displayStep: dto.display_step,
      currency: 'EUR', // Fixed because we don't have this information from BE
      type: dto.type,
      isLimitedToMainProduct: dto.is_limited_to_main_product_quantity,
      mainProductId: dto.main_product_id,
      isMutuallyExclusive: dto.is_mutually_exclusive,
      seatTypes: null,
      order: dto.order,
      group: null,
      description: dto.description,
      image: dto.media && dto.media.length > 0 ? dto.media[0].url1x : null,
      hasReservation: !!dto.has_reservation,
      supplement_ids: dto.supplement_ids,
      required_payment_method_id: dto.required_payment_method_id,
      is_cineville_ticket: dto.is_cineville_ticket,
      is_subscription_ticket: dto.is_subscription_ticket,
      availability: null,
      predefinedAmounts: null,
    };
  }

  private mapToProductModel(dto: ProductResponse): Product {
    return {
      id: dto.id,
      companyId: dto.company_id,
      name: dto.public_name,
      price: dto.current_price?.total_amount,
      priceBefore: dto.current_price?.regular_amount,
      currency: 'EUR',
      type: dto.type,
      seatTypes: dto.seat_type_ids ?? [],
      order: dto.order,
      description: dto.description,
      group: this.mapToPrimaryGroup(dto.groups),
      image: dto.media && dto.media.length > 0 ? dto.media[0].url1x : null,
      hasReservation: !!dto.has_reservation,
      supplement_ids: dto.supplement_ids,
      required_payment_method_id: dto.required_payment_method_id,
      is_cineville_ticket: dto.is_cineville_ticket,
      is_subscription_ticket: dto.is_subscription_ticket,
      availability: this.mapToProductAvailability(dto.availability),
      predefinedAmounts: this.mapToPredefinedAmounts(dto),
    };
  }

  /**
   * The amounts a product is sold at instead of its own price, or null when it is priced normally —
   * which is every product but a gift card configured for them. Left in the order the API returned
   * (already sorted by the value's own `order`).
   */
  private mapToPredefinedAmounts(dto: ProductResponse): ProductPredefinedAmount[] | null {
    if (!dto.show_most_frequent_values || !dto.most_frequent_values?.length) {
      return null;
    }
    return dto.most_frequent_values.map((value) => ({ id: value.id, value: roundToCents(value.value) }));
  }

  /**
   * The primary group — the first of the row's groups, which the backend has already sorted by the
   * group's own order and name. Null when the product is in no active group; a group without a name
   * is treated the same way, since there would be nothing to head its section with.
   */
  private mapToPrimaryGroup(dtos: ProductGroupDto[] | null): ProductGroup | null {
    const primary = dtos?.[0];
    if (!primary?.name) {
      return null;
    }
    return { id: primary.id, title: primary.name, order: primary.order };
  }

  private mapToProductAvailability(dto: ProductAvailabilityDto | null): ProductAvailability | null {
    if (!dto) return null;
    return {
      kind: dto.kind,
      available: dto.available,
      total: dto.total,
      soldOut: dto.sold_out,
      almostSoldOut: dto.almost_sold_out,
      showId: dto.show_id,
    };
  }
}
